<template>
  <v-container class="applications-application">
    <can I="read" a="OpportunityApplication">
      <v-card-text class="text-xs-left pt-0">
        <v-layout>
          <v-menu
            :close-on-content-click="false"
            max-height="50vh"
            max-width="400"
            offset-y
            bottom
            right
          >
            <v-btn slot="activator" small color="secondary">
              <v-icon small left>mdi-folder</v-icon>Documents
            </v-btn>
            <v-card dark>
              <ApplicationDocuments :application="application" @refresh="refresh" />
            </v-card>
          </v-menu>
          <v-menu
            v-if="application.questionnaires && application.questionnaires[0]"
            :close-on-content-click="false"
            max-height="50vh"
            max-width="400"
            bottom
            offset-y
          >
            <v-btn slot="activator" small color="secondary">
              <v-icon small left>mdi-forum</v-icon>Prescreening Questions
            </v-btn>
            <v-card dark>
              <ApplicationQuestionnaire :value="application.questionnaires" />
            </v-card>
          </v-menu>
          <v-menu
            v-if="showPsychometrics"
            :close-on-content-click="false"
            max-width="400"
            max-height="50vh"
            offset-y
            bottom
          >
            <v-btn slot="activator" small color="secondary">
              <v-icon small left>mdi-account-search</v-icon>Assessments
            </v-btn>
            <ApplicationPsychometrics :application="application" />
          </v-menu>

          <ApplicationsExportSelector v-if="application.opportunity" :value="[application.id]">
            <v-btn slot="activator" color="secondary" small>
              <v-icon small left>mdi-file-pdf-box</v-icon>Export
            </v-btn>
          </ApplicationsExportSelector>

          <v-spacer />

          <div class="text-xs-right">
            <!-- Eligibility Details -->
            <v-menu
              slot="activator"
              v-if="application.eligibility && application.eligibility.eligible === false"
              offset-y
              left
            >
              <v-btn slot="activator" color="error" round small>
                <v-icon small left>mdi-account-alert</v-icon>
                Ineligible
              </v-btn>
              <v-card>
                <v-subheader :value="true"> Application no longer eligible </v-subheader>
              </v-card>
            </v-menu>

            <!-- Completion Details -->
            <v-menu offset-y left slot="activator" v-else>
              <v-btn
                slot="activator"
                v-if="application.actionItems && application.actionItems[0]"
                color="warning"
                round
                small
              >
                <v-icon small left>mdi-account-question</v-icon>
                Incomplete
              </v-btn>
              <v-btn slot="activator" v-else color="success" round small>
                <v-icon small left>mdi-account-check</v-icon> Complete
              </v-btn>
              <v-card> <ApplicationCompletionDetails :application="application" /> </v-card>
            </v-menu>
          </div>
        </v-layout>
      </v-card-text>

      <v-card-text class="text-xs-center" v-if="application.profile">
        <v-layout row wrap align-center justify-space-between>
          <v-flex xs10 offset-xs1 d-flex md6 offset-md0>
            <v-layout align-center justify-space-around row wrap fill-height>
              <v-flex xs12>
                <v-progress-circular
                  :value="parseInt(application.completion)"
                  :color="application.complete ? 'info' : 'warning'"
                  :width="10"
                  :rotate="-90"
                  size="150"
                >
                  <v-avatar size="140">
                    <img :src="application.profile.avatar" />
                  </v-avatar>
                </v-progress-circular>
                <div style="position: relative; margin-top: -2rem; margin-right: -8rem;">
                  <Flag icon v-if="avatarFlag" :value="avatarFlag" />
                  <can I="create" a="Flag">
                    <CreateFlag
                      v-if="!avatarFlag"
                      :target="{ ...application.profile, type: 'Profile' }"
                      :profile="application.profile"
                      :filter="['avatar']"
                      @done="refresh"
                    >
                      <v-btn icon flat color="error" slot="activator">
                        <v-icon>mdi-flag</v-icon>
                      </v-btn>
                    </CreateFlag>
                  </can>
                </div>
              </v-flex>
              <v-flex xs12 class="headline mt-2" v-if="application.profile">
                {{ `${application.profile.firstName} ${application.profile.lastName}` }}
              </v-flex>
              <v-flex xs12 class="rating-wrapper my-2">
                <ApplicationRating :application="application" :readonly="isWithdrawn" />
              </v-flex>

              <v-flex xs12>
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    @click="copiedToast('email')"
                    v-clipboard:copy="application.profile.email"
                    color="secondary"
                    icon
                    small
                  >
                    <v-icon small>mdi-email</v-icon>
                  </v-btn>
                  <span> Click to copy {{ application.profile.email }} </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    @click="copiedToast('mobile')"
                    v-clipboard:copy="application.profile.mobileNumber"
                    color="secondary"
                    icon
                    small
                  >
                    <v-icon small>mdi-phone</v-icon>
                  </v-btn>
                  <span> Click to copy +{{ application.profile.mobileNumber }} </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-menu offset-y slot="activator">
                    <v-btn slot="activator" icon small color="secondary">
                      <v-icon small>mdi-information-variant</v-icon>
                    </v-btn>
                    <v-card dark>
                      <PersonalDetails
                        :actionItems="application.actionItems"
                        :profile="application.profile"
                      />
                    </v-card>
                  </v-menu>
                  <span> View Personal Information </span>
                </v-tooltip>
                <v-tooltip bottom v-if="application.opportunity && application.opportunity.locations">
                  <v-menu offset-y slot="activator">
                    <v-btn slot="activator" icon small color="secondary">
                      <v-icon small>mdi-map-marker</v-icon>
                    </v-btn>
                    <v-card dark>
                      <!-- Applications Locations -->
                      <v-list>
                        <v-list-tile
                          v-for="location in application.opportunity.locations"
                          :key="location.id"
                        >
                          <v-list-tile-avatar>
                            <v-tooltip bottom>
                              <v-icon slot="activator">mdi-map-marker</v-icon>
                              <span>Application Location</span>
                            </v-tooltip>
                          </v-list-tile-avatar>
                          <v-list-tile-content>
                            <v-list-tile-title class="text-xs-left">
                              <span class="ml-3 ">{{ location.address }}</span>
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </v-list>
                    </v-card>
                  </v-menu>
                  <span> View Application Locations </span>
                </v-tooltip>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex d-flex xs10 offset-xs1 md5 offset-md0>
            <can I="list" a="Note">
              <ApplicationNotes :application="application" />
            </can>
          </v-flex>
          <v-flex xs1></v-flex>
        </v-layout>
      </v-card-text>

      <v-card-text ref="application-profile" class="pa-0" v-if="application && application.profile">
        <v-layout row wrap>
          <v-flex
            v-if="application.profile.biography && application.profile.biography.length > 0"
            xs10 offset-xs1
          >
            <v-toolbar flat color="transparent">
              <div class="headline px-3">
                Biography
              </div>
              <v-spacer />
              <Flag icon v-if="bioFlag" :value="bioFlag" />
              <can I="create" a="Flag">
                <CreateFlag
                  v-if="!bioFlag"
                  :target="{ ...application.profile, type: 'Profile' }"
                  :profile="application.profile"
                  :filter="['biography']"
                  @done="refresh"
                >
                  <v-btn icon flat color="error" slot="activator">
                    <v-icon>mdi-flag</v-icon>
                  </v-btn>
                </CreateFlag>
              </can>
            </v-toolbar>

            <v-card-text class="text-xs-justify">
              <markdown :html="false" :source="application.profile.biography" class="markdown" />
            </v-card-text>
          </v-flex>
        </v-layout>
        <Experience
          v-if="application.profile.experiences && application.profile.experiences[0]"
          :experiences="application.profile.experiences"
        />
        <Qualifications
          v-if="application.profile.profileQualifications && application.profile.profileQualifications[0]"
          :profile="application.profile"
          :actionItems="application.actionItems"
          @refresh="refresh"
        />
        <AdditionalInformation :profile="application.profile" />
      </v-card-text>
    </can>
  </v-container>
</template>

<script>
// Components
import AdditionalInformation from '@/components/applications_tracker/profile/AdditionalInformation'
import ApplicationCompletionDetails from '@/components/applications_tracker/applications/ApplicationCompletionDetails'
import ApplicationDocuments from '@/components/applications_tracker/applications/ApplicationDocuments'
import ApplicationNotes from '@/components/applications_tracker/applications/ApplicationNotes'
import ApplicationPsychometrics from '@/components/applications_tracker/applications/ApplicationPsychometrics'
import ApplicationQuestionnaire from '@/components/applications_tracker/applications/ApplicationQuestionnaire'
import ApplicationRating from '@/components/applications_tracker/applications/ApplicationRating'
import ApplicationsExportSelector from '@/components/applications_tracker/applications/ApplicationsExportSelector'
import CreateFlag from '@/components/flags/CreateFlag'
import Experience from '@/components/applications_tracker/profile/Experience'
import Flag from '@/components/flags/Flag'
import Markdown from 'vue-markdown'
import PersonalDetails from '@/components/applications_tracker/profile/PersonalDetails'
import Qualifications from '@/components/applications_tracker/profile/qualifications/Qualifications'

// Vue Component
export default {
  components: {
    AdditionalInformation,
    ApplicationCompletionDetails,
    ApplicationDocuments,
    ApplicationNotes,
    ApplicationPsychometrics,
    ApplicationQuestionnaire,
    ApplicationRating,
    ApplicationsExportSelector,
    CreateFlag,
    Experience,
    Flag,
    Markdown,
    PersonalDetails,
    Qualifications,
  },

  props: ['application'],

  computed: {
    isWithdrawn() {
      return this.application.status == 'Withdrawn'
    },

    flags() {
      if (!_.get(this, 'application.profile.flags[0]')) return []
      return this.application.profile.flags.filter(f =>
        f.targetType === 'Profile' &&
        f.targetId === this.application.profile.id
      )
    },
    bioFlag() {
      var filtered = this.flags.filter(f => f.key.indexOf('biography') >= 0)
      return filtered.length ? filtered[0] : null
    },
    avatarFlag() {
      var filtered = this.flags.filter(f => f.key.indexOf('avatar') >= 0)
      return filtered.length ? filtered[0] : null
    },
    showPsychometrics() {
      return this.application.psychometrics && this.application.psychometrics.filter(p => p.show || p.provider != 'Odyssey' || p.aggregate >= 2).length
    }
  },

  methods: {
    copiedToast(value) {
      this.$store.dispatch('snackbarShow', `Copied ${value}.`)
    },

    refresh() {
      return this.$store.dispatch('admin/applications/fetch', this.application.id)
    },
  },
}
</script>

<style>
.applications-application div.rating-wrapper fieldset {
  position: relative;
}
</style>
